import { Injectable } from '@angular/core';
import { cloneDeep, merge } from 'lodash';
import { CardConfig } from '@framework/analytics/models/card-model';
import { DEFAULT_THEME } from '@framework/analytics/defaults/theme.defaults';
import { applyChartPresentationWireFromApi } from '@framework/analytics/utils/card-presentation-wire.util';
import { applyPaletteWireFromApi } from '@framework/analytics/utils/card-palette-wire.util';
import {
	PageConfig,
	pageHiddenFromApi,
} from '@framework/analytics/models/page.model';
import { PaletteConfig } from '@framework/analytics/models/palette.model';
import { ThemeConfig } from '@framework/analytics/models/theme.model';
import { ViewConfig } from '@framework/analytics/models/view.model';
import { CardService } from '@framework/analytics/services/card.service';
import { PaletteService } from '@framework/analytics/services/palette.service';
import { normalizeThemeConfig } from '@framework/analytics/utils/normalize-theme-typography.util';
import { Observable } from 'rxjs';
import { RequestService } from './request.service';
import { SyncService, SyncEvent } from './sync.service';
import { log } from '@jepto/shared/utils/logger';
import { requireAnalyticsEventsChannel } from '@framework/analytics/utils/report-id-encoding.util';
import { buildGetPageDataRequest } from '@framework/analytics/graphql/queries/get-page-data';
import { fetchGetCardItem } from '@framework/analytics/graphql/queries/fetch-get-card.util';
import { applyCardDataWireFromApi } from '@framework/analytics/utils/card-data-gql.util';
import { queryGetCardData } from '@framework/analytics/graphql/queries/get-card-data';
import { queryGetTheme } from '@framework/analytics/graphql/queries/get-theme';
import type { FetchPageDataOptions } from '@framework/analytics/models/fetch-page-data-options.model';
import type { PageCardFilterInput } from '@framework/analytics/models/page-filter.model';
import { PageDataRefreshCoordinatorService } from '@framework/analytics/services/page-data-refresh-coordinator.service';
import {
	AnalyticsDataLatencyService,
	dataSourceServicesFromCardConfig,
} from '@framework/analytics/services/analytics-data-latency.service';
import { AnalyticsDataRequesterService } from '@framework/analytics/services/analytics-data-requester.service';

/**
 * Analytics Service
 *
 * Provides GraphQL queries for analytics data and WebSocket subscriptions.
 *
 * ## Data Fetching Patterns
 *
 * This service provides Promise-based methods for GraphQL queries, designed
 * to work with Angular's `resource()` API or async/await patterns.
 *
 * For real-time data, `subscribeToViewData()` returns an Observable for
 * WebSocket streaming - a proper use case for RxJS.
 *
 * @example Using with resource()
 * ```typescript
 * viewConfigResource = resource({
 *   params: () => ({ accountId, viewId, pageId }),
 *   loader: ({ params }) =>
 *     analyticsService.fetchViewConfig(params.accountId, params.viewId, params.pageId ?? null)
 * });
 * ```
 */
@Injectable({
	providedIn: 'root',
})
export class AnalyticsService {
	constructor(
		private requestService: RequestService,
		private syncService: SyncService,
		private pageDataRefreshCoordinator: PageDataRefreshCoordinatorService,
		private analyticsDataLatency: AnalyticsDataLatencyService,
		private analyticsDataRequester: AnalyticsDataRequesterService,
		private paletteService: PaletteService,
		private cardService: CardService
	) {}

	private throwIfGraphQlError(gqlResponse: any, operation: string): void {
		if (
			!Array.isArray(gqlResponse?.errors) ||
			gqlResponse.errors.length === 0
		)
			return;
		const firstError = gqlResponse.errors[0];
		const message = firstError?.message ?? 'Unknown GraphQL error';
		throw new Error(`[Analytics:${operation}] ${message}`);
	}

	private requirePath<T>(
		gqlResponse: any,
		operation: string,
		selector: (response: any) => T | null | undefined
	): T {
		this.throwIfGraphQlError(gqlResponse, operation);
		const value = selector(gqlResponse);
		if (value == null) {
			throw new Error(
				`[Analytics:${operation}] Missing data in GraphQL response`
			);
		}
		return value;
	}

	/**
	 * Maps a `listItems.themes.items` row or embedded `getView.theme` to {@link ThemeConfig}.
	 * API uses `paletteId`; framework model uses `palette` (string).
	 */
	private mapListThemeRowToConfig(
		raw: Record<string, unknown>
	): ThemeConfig | null {
		const themeId =
			typeof raw['themeId'] === 'string' ? raw['themeId'].trim() : '';
		if (!themeId) {
			return null;
		}

		const label =
			typeof raw['label'] === 'string' ? raw['label'].trim() : '';

		const base = cloneDeep(DEFAULT_THEME);
		const nested = raw['palette'] as { paletteId?: string } | undefined;
		const paletteFromScalar =
			typeof raw['paletteId'] === 'string' ? raw['paletteId'].trim() : '';
		const paletteFromNested =
			typeof nested?.paletteId === 'string'
				? nested.paletteId.trim()
				: '';
		const palette = paletteFromScalar || paletteFromNested || base.palette;

		merge(base, {
			themeId,
			...(label ? { label } : {}),
			palette,
			maxWidth:
				typeof raw['maxWidth'] === 'string' &&
				raw['maxWidth'].length > 0
					? raw['maxWidth']
					: base.maxWidth,
			padding:
				typeof raw['padding'] === 'string'
					? raw['padding']
					: typeof raw['padding'] === 'number'
						? String(raw['padding'])
						: base.padding,
		});

		if (raw['card'] && typeof raw['card'] === 'object') {
			merge(base.card, raw['card'] as object);
		}
		if (raw['colors'] && typeof raw['colors'] === 'object') {
			merge(base.colors, raw['colors'] as object);
		}
		if (raw['typography'] && typeof raw['typography'] === 'object') {
			merge(base.typography, raw['typography'] as object);
		}

		return normalizeThemeConfig(base);
	}

	#patchPaletteFromThemeItem(raw: Record<string, unknown>): void {
		const paletteRow = raw.palette as
			| {
					paletteId?: string;
					label?: string;
					light?: string[];
					dark?: string[];
			  }
			| undefined;
		const paletteId =
			(typeof raw.paletteId === 'string' ? raw.paletteId.trim() : '') ||
			paletteRow?.paletteId?.trim() ||
			'';
		if (!paletteId || !paletteRow) {
			return;
		}
		const light = paletteRow.light;
		const dark = paletteRow.dark;
		if (!Array.isArray(light) || light.length === 0) {
			return;
		}
		if (!Array.isArray(dark) || dark.length === 0) {
			return;
		}

		const palette: PaletteConfig = {
			paletteId,
			label: paletteRow.label?.trim() || paletteId,
			light: [...light],
			dark: [...dark],
		};
		this.paletteService.patchAvailablePalette(palette);
	}

	// ============================================
	// Promise-based methods (for use with resource() or async/await)
	// ============================================

	/**
	 * Fetch view configuration for the public analytics app.
	 * View-level fields must stay aligned with `queryGetView` in
	 * `projects/jepto/src/app/features/reports/graphql/queries/get-view.ts`
	 * (e.g. `dateRange`, embedded `theme { ... }`).
	 */
	async fetchViewConfig(
		accountId: string,
		viewId: string,
		/** Omit or pass `null` for report-only URLs; resolver returns the full view (all pages). */
		pageId: string | null
	): Promise<ViewConfig> {
		const query = `
      query AnalyticsView($viewId: ID!, $accountId: ID, $pageId: ID) {
        getItem {
            view(viewId: $viewId, accountId: $accountId, pageId: $pageId){
                accountId
                viewId
                title
                description
                clientId
                timezone
              mode
              themeId
              dataSources
              dateRange
              globalFilters
              theme {
                themeId
                card {
                  borderRadius
                  borderWidth
                  padding
                  shadow
                  spacing
                }
                colors {
                  dark {
                    background
                    border {
                      primary
                      secondary
                    }
                    card {
                      background
                      shadow
                    }
                    status {
                      danger
                      info
                      success
                      warning
                    }
                    text {
                      link
                      primary
                      secondary
                    }
                  }
                  light {
                    background
                    border {
                      primary
                      secondary
                    }
                    card {
                      background
                      shadow
                    }
                    status {
                      danger
                      info
                      success
                      warning
                    }
                    text {
                      link
                      primary
                      secondary
                    }
                  }
                }
                label
                maxWidth
                padding
                paletteId
                palette {
                  dark
                  label
                  light
                  paletteId
                }
                typography {
                  baseSize
                  fontFamily
                  card {
                    headingWeight
                    subheadingWeight
                  }
                  size {
                    lg
                    md
                    sm
                    xl
                    xs
                    xxl
                    xxs
                  }
                  weight {
                    bold
                    light
                    medium
                    regular
                    semibold
                  }
                }
              }
              pages {
                  pageId
                  order
                  title
                  description
                  icon
                  isHidden
                  viewId
                  revisions
                  filters {
                      date {
                          type
                          show
                          default {
                              start
                              end
                          }
                      }
                      segments {
                          type
                          options {
                              value
                              label
                          }
                          default
                          show
                          applyToAllLayouts
                      }
                  }
                  layout {
                      cardId
                      cardType
                      chartType
                      x
                      y
                      h
                      w
                  }

              }
              navigation {
                  type
                  position
                  showIcons
                  collapsible
              }
              access {
                  roles
                  users
                  public
              }
              export {
                  formats
                  scheduling {
                      enabled
                      frequencies
                  }
              }
              dateCreated
              dateUpdated
              userId
            }
        }
      }
    `;
		const gqlResponse = await this.requestService.execute(query, {
			accountId,
			viewId,
			pageId,
		});
		const view = this.requirePath(
			gqlResponse,
			'getViewConfig',
			r => r?.data?.getItem?.view
		);
		const rawTheme = view.theme as
			| Record<string, unknown>
			| null
			| undefined;
		const theme =
			rawTheme && typeof rawTheme === 'object'
				? this.mapListThemeRowToConfig(rawTheme)
				: null;
		if (rawTheme && typeof rawTheme === 'object') {
			this.#patchPaletteFromThemeItem(rawTheme);
		}
		const { theme: _rawThemeOmit, ...viewWithoutRawTheme } = view;
		const normalizedView = {
			...viewWithoutRawTheme,
			...(theme ? { theme } : {}),
		};
		if (!normalizedView.pages?.length) {
			return normalizedView;
		}
		return {
			...normalizedView,
			pages: normalizedView.pages.map(page => ({
				...page,
				hidden: pageHiddenFromApi(page),
			})),
		};
	}

	/**
	 * Fetch page configuration
	 */
	async fetchPageConfig(
		accountId: string,
		pageId: string
	): Promise<Partial<PageConfig>> {
		const query = `
      query getPageConfig($pageId: ID!, $accountId: ID!) {
        getItem {
          reportPage(pageId: $pageId, accountId: $accountId){
            pageId
            order
            title
            description
            icon
            isHidden
            viewId
            revisions
            filters {
                date {
                    type
                    show
                    default {
                        start
                        end
                    }
                }
                segments {
                    type
                    options {
                        value
                        label
                    }
                    default
                    show
                    applyToAllLayouts
                }
            }
            layout {
                cardId
                cardType
                chartType
                x
                y
                h
                w
            }
            }
          }
        }
    `;
		const gqlResponse = await this.requestService.execute(query, {
			pageId,
			accountId,
		});
		log('GQL Response[getPageConfig]', gqlResponse);
		const raw = this.requirePath(
			gqlResponse,
			'getPageConfig',
			r => r?.data?.getItem?.reportPage
		);
		return {
			...raw,
			hidden: pageHiddenFromApi(raw),
		};
	}

	/**
	 * Fetch card configuration from GraphQL `getItem.card`.
	 *
	 * @returns Card fields from the API after {@link applyPaletteWireFromApi} runs on the payload.
	 * @throws {Error} If the response includes GraphQL errors, or `getItem.card` is null/undefined or not an object.
	 */
	async fetchCardConfig(
		cardId: string,
		viewId: string,
		accountId: string,
		/** Page scope for `getItem.card` — must match main app `queryGetCard` variables (see report-editor.store). May be `null` for report-root flows. */
		pageId: string | null
	): Promise<Partial<CardConfig>> {
		const { requestId, requester } =
			await this.analyticsDataRequester.buildRequestContext();
		this.analyticsDataLatency.markRequest({
			requestId,
			operation: 'getCard',
			cardId,
			pageId: pageId ?? undefined,
			viewId,
			accountId,
			dataSourceServices: dataSourceServicesFromCardConfig(
				this.cardService.cardConfig(cardId)()
			),
		});
		const gqlResponse = await fetchGetCardItem(
			(query, variables) => this.requestService.execute(query, variables),
			{
				viewId,
				cardId,
				accountId,
				pageId: pageId ?? null,
			},
			{ requestId, requester }
		);
		this.throwIfGraphQlError(gqlResponse, 'getCardConfig');
		const card = gqlResponse?.data?.getItem?.card;
		if (card == null || typeof card !== 'object') {
			const gqlErrors =
				Array.isArray(gqlResponse?.errors) &&
				gqlResponse.errors.length > 0
					? gqlResponse.errors
							.map((e: { message?: string }) => e.message ?? '?')
							.join('; ')
					: 'none';
			throw new Error(
				`[Analytics:getCardConfig] getItem.card is null or missing. cardId=${cardId} pageId=${String(pageId)} viewId=${viewId} accountId=${accountId}. GraphQL errors: ${gqlErrors}`
			);
		}
		applyPaletteWireFromApi(
			card as Partial<CardConfig> & Record<string, unknown>
		);
		applyChartPresentationWireFromApi(
			card as Partial<CardConfig> & Record<string, unknown>
		);
		applyCardDataWireFromApi(card as Record<string, unknown>);
		return card;
	}

	/**
	 * Batch-hydrates or refreshes page card data via `getPageData`; optional `pageFilters`
	 * scope chart/table queries. Results are pushed on the view WebSocket channel, not in
	 * the GraphQL response.
	 *
	 * See framework `card-data.mdx` — “Page runtime hydration” for `getCard` vs
	 * `getPageData` ordering and the WebSocket requestId floor.
	 *
	 * @param options See {@link FetchPageDataOptions}.
	 */
	async fetchPageData(
		accountId: string,
		pageId: string,
		timeDimension: { dateRange: string[] },
		pageFilters: PageCardFilterInput[] = [],
		options: FetchPageDataOptions = {}
	): Promise<boolean | null> {
		if (!timeDimension.dateRange?.length) {
			return null;
		}

		const requestId =
			options.requestId ?? this.analyticsDataRequester.createRequestId();
		if (options.advanceWebSocketFloor !== false) {
			this.pageDataRefreshCoordinator.setRefreshFloor(pageId, requestId);
		}
		const { requester } =
			await this.analyticsDataRequester.buildRequestContext(requestId);
		const { query, variables } = buildGetPageDataRequest(
			{
				accountId,
				pageId,
				timeDimension,
				requestId,
				requester,
				...(options.refresh === true ? { refresh: true } : {}),
			},
			pageFilters
		);
		this.analyticsDataLatency.markRequest({
			requestId,
			operation: 'getPageData',
			pageId,
			accountId,
			dataSourceServices: options.dataSourceServices,
		});
		const gqlResponse = await this.requestService.execute(query, variables);
		log('GQL Response[getPageData]', gqlResponse);
		return this.requirePath(
			gqlResponse,
			'getPageData',
			r => r?.data?.getItem?.pageData
		);
	}

	async fetchCardData(
		cardId: string,
		timeDimension: { dateRange: string[] },
		accountId: string
	): Promise<any> {
		if (!timeDimension.dateRange) return null;

		const { requestId, requester } =
			await this.analyticsDataRequester.buildRequestContext();
		this.analyticsDataLatency.markRequest({
			requestId,
			operation: 'getCardData',
			cardId,
			accountId,
			dataSourceServices: dataSourceServicesFromCardConfig(
				this.cardService.cardConfig(cardId)()
			),
		});
		const gqlResponse = await this.requestService.execute(
			queryGetCardData,
			{
				cardId,
				timeDimension,
				accountId,
				requestId,
				requester,
			}
		);
		log('GQL Response[getCardData]', gqlResponse);
		return this.requirePath(
			gqlResponse,
			'getCardData',
			r => r?.data?.getItem?.cardData
		);
	}

	/**
	 * Fetch a single theme by id (fallback when AnalyticsView omits embedded `theme`).
	 */
	async fetchTheme(
		accountId: string,
		themeId: string
	): Promise<ThemeConfig | null> {
		const normalizedAccountId = accountId?.trim();
		const normalizedThemeId = themeId?.trim();
		if (!normalizedAccountId || !normalizedThemeId) {
			return null;
		}

		const gqlResponse = await this.requestService.execute(queryGetTheme, {
			accountId: normalizedAccountId,
			themeId: normalizedThemeId,
		});
		log('GQL Response[getTheme]', gqlResponse);
		this.throwIfGraphQlError(gqlResponse, 'getTheme');
		const raw = gqlResponse?.data?.getItem?.theme as
			| Record<string, unknown>
			| null
			| undefined;
		if (!raw) {
			return null;
		}
		this.#patchPaletteFromThemeItem(raw);
		return this.mapListThemeRowToConfig(raw);
	}

	/**
	 * Fetch palettes
	 */
	async fetchPalettes(accountId: string): Promise<PaletteConfig[]> {
		const query = `
      query listPalettes($accountId: ID){
        listItems {
          palettes(accountId: $accountId) {
            items{
              label
              dark
              light
              paletteId
            }
          }
        }
      }`;
		const gqlResponse = await this.requestService.execute(query, {
			accountId,
		});
		log('GQL Response[getPalettes]', gqlResponse);
		this.throwIfGraphQlError(gqlResponse, 'getPalettes');
		return gqlResponse?.data?.listItems?.palettes?.items ?? [];
	}

	/**
	 * Fetch single palette
	 */
	async fetchPalette(
		accountId: string,
		paletteId: string
	): Promise<PaletteConfig> {
		const query = `
      query getPalette($accountId: ID, $paletteId: ID){
        getItem{
          palette(accountId: $accountId, paletteId: $paletteId) {
            label
            dark
            light
          }
        }
      }`;
		const gqlResponse = await this.requestService.execute(query, {
			accountId,
			paletteId,
		});
		log('GQL Response[getPalette]', gqlResponse);
		return this.requirePath(
			gqlResponse,
			'getPalette',
			r => r?.data?.getItem?.palette
		);
	}

	// ============================================
	// Observable-based methods (WebSocket subscriptions only)
	// WebSocket streams are a proper use case for RxJS Observables
	// ============================================

	/**
	 * Subscribe to real-time view data updates via WebSocket.
	 * This returns an Observable because WebSocket connections are
	 * continuous push-based streams - the appropriate pattern for RxJS.
	 *
	 * The Observable emits SyncEvent objects with different types:
	 * - 'connected': WebSocket connection established
	 * - 'data': Card data received from backend
	 * - 'error': Connection or data error
	 * - 'disconnected': WebSocket disconnected
	 */
	subscribeToViewData(
		accountId: string,
		viewId: string
	): Observable<SyncEvent> {
		const channel = requireAnalyticsEventsChannel(accountId, viewId);
		log('AnalyticsService.subscribeToViewData', {
			accountId,
			viewId,
			channel,
		});
		return this.syncService.getSyncEvents(channel);
	}
}
